import { Component, signal, input, output, computed, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth.service';

@Component({
  selector: 'app-settings-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <!-- Overlay -->
    @if (isOpen()) {
      <div class="settings-overlay" (click)="closeSidebar()" (keydown.escape)="closeSidebar()"></div>
    }

    <!-- Sidebar -->
    <aside
      [class]="'settings-sidebar ' + (isOpen() ? 'open' : 'closed')"
      [attr.aria-label]="'Panel de configuración'"
      role="complementary"
    >
      <!-- Header -->
      <header class="sidebar-header">
        <div class="header-content">
          <div class="header-title">
            <h2>Configuración</h2>
          </div>

          <div class="header-actions">
            <button class="close-btn" (click)="closeSidebar()" aria-label="Cerrar panel" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      <!-- Content -->
      <main class="sidebar-content" role="main">
        <!-- User Info Section -->
        <section class="settings-section user-section">
          <div class="section-header">
            <div class="section-title">
              <i class="fas fa-user"></i>
              <span>Perfil de Usuario</span>
            </div>
          </div>

          <div class="user-info-card">
            <div class="user-avatar">
              <i class="fas fa-user-circle"></i>
            </div>
            <div class="user-details">
              <div class="user-name">{{ authService.currentUser()?.username }}</div>
              <div class="user-role">{{ authService.userRole() }}</div>
              <div class="user-email">{{ authService.currentUser()?.email || 'usuario@yurest.com' }}</div>
            </div>
          </div>
        </section>

        <div class="section-divider"></div>

        <!-- Account Actions Section -->
        <section class="settings-section actions-section">
          <div class="section-header">
            <div class="section-title">
              <i class="fas fa-shield-alt"></i>
              <span>Cuenta</span>
            </div>
          </div>

          <div class="actions-list">
            @if (authService.userRole()?.toLowerCase() === 'developer') {
              <button class="action-item safe-action" (click)="navigateToQueues()">
                <div class="action-content">
                  <div class="action-icon">
                    <i class="fas fa-stream"></i>
                  </div>
                  <div class="action-info">
                    <span class="action-label">Colas</span>
                    <span class="action-description">Monitorear estado de las colas PGMQ</span>
                  </div>
                </div>
              </button>
            }

            <button class="action-item safe-action">
              <div class="action-content">
                <div class="action-icon">
                  <i class="fas fa-download"></i>
                </div>
                <div class="action-info">
                  <span class="action-label">Exportar Datos</span>
                  <span class="action-description">Descargar información personal</span>
                </div>
              </div>
            </button>

            <button class="action-item danger-action" (click)="onLogout()">
              <div class="action-content">
                <div class="action-icon">
                  <i class="fas fa-sign-out-alt"></i>
                </div>
                <div class="action-info">
                  <span class="action-label">Cerrar Sesión</span>
                  <span class="action-description">Salir de la aplicación</span>
                </div>
              </div>
            </button>
          </div>
        </section>
      </main>
    </aside>
  `,
  styles: [
    `
      /* Overlay */
      .settings-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.3);
        backdrop-filter: blur(4px);
        z-index: 1100;
        opacity: 0;
        animation: overlayIn 0.3s ease forwards;
      }

      @keyframes overlayIn {
        to {
          opacity: 1;
        }
      }

      /* Sidebar */
      .settings-sidebar {
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        width: 420px;
        max-width: 100vw;
        background: var(--bg-primary);
        border-left: 1px solid var(--border-primary);
        box-shadow: var(--shadow-xl);
        z-index: 1200;
        display: flex;
        flex-direction: column;
        transform: translateX(100%);
        transition:
          transform 0.3s ease,
          box-shadow 0.3s ease;
        overflow: hidden;
      }

      .settings-sidebar.open {
        transform: translateX(0);
      }

      .settings-sidebar.closed {
        transform: translateX(100%);
      }

      /* Header */
      .sidebar-header {
        flex-shrink: 0;
        background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
        border-bottom: 1px solid var(--border-primary);
        padding: var(--space-xl);
        box-shadow: var(--shadow-sm);
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--space-md);
      }

      .header-title h2 {
        font-size: var(--text-xl);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        margin: 0;
        line-height: var(--leading-snug);
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
      }

      .close-btn {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        border-radius: var(--radius-md);
        transition: all 0.2s ease;
        font-size: var(--text-sm);
      }

      .close-btn:hover {
        background: var(--bg-secondary);
        color: var(--red-600);
        transform: scale(1.1);
      }

      .close-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      /* Content */
      .sidebar-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 0;
      }

      .sidebar-content::-webkit-scrollbar {
        width: 6px;
      }

      .sidebar-content::-webkit-scrollbar-track {
        background: var(--bg-secondary);
      }

      .sidebar-content::-webkit-scrollbar-thumb {
        background: var(--text-muted);
        border-radius: var(--radius-xs);
      }

      .sidebar-content::-webkit-scrollbar-thumb:hover {
        background: var(--text-secondary);
      }

      /* Settings Sections */
      .settings-section {
        padding: var(--space-xl);
      }

      .section-divider {
        height: 1px;
        background: var(--border-primary);
        margin: 0;
      }

      .section-header {
        margin-bottom: var(--space-lg);
      }

      .section-title {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        text-transform: uppercase;
        letter-spacing: var(--letter-wide);
      }

      .section-title i {
        color: var(--red-500);
        font-size: var(--text-base);
      }

      /* User Info Section */
      .user-info-card {
        display: flex;
        align-items: center;
        gap: var(--space-lg);
        padding: var(--space-lg);
        background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-accent) 100%);
        border: 1px solid var(--border-accent);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-sm);
      }

      .user-avatar {
        width: 60px;
        height: 60px;
        background: var(--bg-primary);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border: 3px solid var(--red-200);
        box-shadow: var(--shadow-sm);
      }

      .user-avatar i {
        font-size: var(--text-3xl);
        color: var(--red-500);
      }

      .user-details {
        flex: 1;
        min-width: 0;
      }

      .user-name {
        font-size: var(--text-lg);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        margin-bottom: var(--space-xs);
        line-height: var(--leading-snug);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .user-role {
        font-size: var(--text-sm);
        color: var(--red-600);
        text-transform: uppercase;
        letter-spacing: var(--letter-wide);
        font-weight: var(--font-semibold);
        line-height: var(--leading-none);
        margin-bottom: var(--space-xs);
      }

      .user-email {
        font-size: var(--text-sm);
        color: var(--text-muted);
        line-height: var(--leading-normal);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Actions */
      .actions-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
      }

      .action-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-lg);
        background: var(--bg-secondary);
        border: 1px solid var(--border-primary);
        border-radius: var(--radius-lg);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all 0.2s ease;
        width: 100%;
        text-align: left;
      }

      .action-item.safe-action:hover {
        background: var(--bg-tertiary);
        border-color: var(--border-secondary);
        color: var(--text-primary);
        transform: translateY(-1px);
        box-shadow: var(--shadow-sm);
      }

      .action-item.danger-action:hover {
        background: var(--bg-danger);
        border-color: var(--red-300);
        color: var(--red-600);
        transform: translateY(-1px);
        box-shadow: var(--shadow-danger);
      }

      .action-content {
        display: flex;
        align-items: center;
        gap: var(--space-lg);
        flex: 1;
      }

      .action-icon {
        width: 40px;
        height: 40px;
        background: var(--bg-primary);
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--border-primary);
        flex-shrink: 0;
        color: var(--text-muted);
      }

      .action-item.danger-action:hover .action-icon {
        background: var(--red-50);
        border-color: var(--red-200);
        color: var(--red-500);
      }

      .action-info {
        display: flex;
        flex-direction: column;
        gap: var(--space-xs);
        flex: 1;
      }

      .action-label {
        font-size: var(--text-base);
        font-weight: var(--font-medium);
        color: var(--text-primary);
        line-height: var(--leading-snug);
      }

      .action-description {
        font-size: var(--text-sm);
        color: var(--text-muted);
        line-height: var(--leading-normal);
      }

      /* Footer */
      .sidebar-footer {
        flex-shrink: 0;
        padding: var(--space-lg);
        border-top: 1px solid var(--border-primary);
        background: var(--bg-secondary);
      }

      .footer-info {
        text-align: center;
      }

      .app-version {
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--text-primary);
        margin: 0 0 var(--space-sm) 0;
      }

      .footer-links {
        font-size: var(--text-xs);
        color: var(--text-muted);
        margin: 0;
        line-height: var(--leading-normal);
      }

      .footer-link {
        color: var(--text-muted);
        text-decoration: none;
        font-weight: var(--font-medium);
        transition: color 0.2s ease;
      }

      .footer-link:hover {
        color: var(--red-500);
        text-decoration: underline;
      }

      .separator {
        margin: 0 var(--space-sm);
        opacity: 0.5;
      }

      /* Mobile Styles */
      @media (max-width: 768px) {
        .settings-sidebar {
          width: 100vw;
          max-width: none;
          top: 0;
          height: 100vh;
          z-index: 1300;
        }

        .settings-overlay {
          z-index: 1250;
          top: 0;
        }

        .sidebar-header {
          padding: var(--space-lg);
        }

        .header-title h2 {
          font-size: var(--text-lg);
        }

        .settings-section {
          padding: var(--space-lg);
        }

        .user-info-card {
          padding: var(--space-md);
          gap: var(--space-md);
        }

        .user-avatar {
          width: 50px;
          height: 50px;
        }

        .user-avatar i {
          font-size: var(--text-2xl);
        }

        .theme-option,
        .action-item {
          padding: var(--space-md);
        }

        .theme-option-content,
        .action-content {
          gap: var(--space-md);
        }

        .theme-icon,
        .action-icon {
          width: 36px;
          height: 36px;
        }

        .sidebar-footer {
          padding: var(--space-md);
        }
      }

      @media (max-width: 480px) {
        .settings-sidebar {
          top: 0;
          height: 100vh;
        }

        .user-name {
          font-size: var(--text-base);
        }

        .theme-label,
        .action-label {
          font-size: var(--text-sm);
        }
      }

      /* Reduced motion support */
      @media (prefers-reduced-motion: reduce) {
        .settings-sidebar,
        .settings-overlay,
        .close-btn,
        .theme-option,
        .action-item {
          transition: none;
          animation: none;
        }

        .close-btn:hover,
        .theme-option:hover,
        .action-item:hover {
          transform: none;
        }
      }

      /* High contrast mode */
      @media (prefers-contrast: high) {
        .settings-sidebar {
          border-left-width: 2px;
        }

        .sidebar-header,
        .section-divider,
        .sidebar-footer {
          border-bottom-width: 2px;
          border-top-width: 2px;
        }

        .user-info-card,
        .theme-option,
        .action-item {
          border-width: 2px;
        }
      }
    `,
  ],
})
export class SettingsSidebarComponent {
  private router = inject(Router);
  authService = inject(AuthService);
  // Input properties
  isOpen = input<boolean>(false);

  // Output events
  close = output<void>();
  logout = output<void>();

  // Methods
  closeSidebar(): void {
    this.close.emit();
  }

  onLogout(): void {
    this.logout.emit();
  }

  navigateToQueues(): void {
    this.router.navigate(['/queues']);
    this.closeSidebar();
  }
}
